import {
  accidentDataApi,
  environmentalDataApi,
  formularABApi,
  formularCApi,
  formularDApi,
} from '../../services/reportsApi.js';
import { FederalStates, ReportTypeIdentifier } from '../../types/reports.js';
import { AccidentDataForm } from './AccidentDataForm.js';
import { EnvironmentalDataForm } from './EnvironmentalDataForm.js';
import { FormularABForm } from './FormularABForm.js';
import { FormularCForm } from './FormularCForm.js';
import { FormularDForm } from './FormularDForm.js';
import { FORMULAR_D_CALC_INPUT_KEYS } from './formularD/catalog.js';
import {
  hydrateAccidentData,
  hydrateEnvironmentalData,
  hydrateFormularAB,
  hydrateFormularC,
  hydrateFormularD,
} from './hydrate.js';

// Die rechenrelevanten Eingabefeld-Listen unten speisen useReportCalc (D-10); nur Typen mit
// Server-Calc setzen ihre Deskriptor-Property, siehe je Konstante unten.
// Die 13 Keys aus AccidentDataForm.tsx CALC_INPUT_KEYS (Unfallstatistik-Live-Vorschau).
const ACCIDENT_CALC_INPUT_KEYS = [
  'hoursWorkedWage', 'hoursWorkedAdmin', 'hoursWorkedTemp',
  'accidentsLostDayWage', 'accidentsLostDayAdmin', 'accidentsLostDayTemp',
  'lostWorkDaysWage', 'lostWorkDaysAdmin', 'lostWorkDaysTemp',
  'employeesTotal', 'employeesAdmin',
  'indirectHoursWorked', 'indirectLtis',
  // C-01: kein Eingabefeld, sondern der eingefrorene KANUE-Wert der Meldung, den
  // /api/reports/calculate sonst nicht kennen kann (siehe hydrateAccidentData). Betrifft
  // ausschliesslich diese Liste -- ENVIRONMENTAL_CALC_INPUT_KEYS/FORMULAR_AB_CALC_INPUT_KEYS
  // bleiben unveraendert.
  'annualHoursTemp',
] as const;

// Wasser-Vorschau (Litmus-Test der Phase, EnvironmentalDataForm.tsx:49). Die Brennstoff-Arrays
// (Phase 30, B-03/B-05/B-09) sind selbst Objekte/Arrays -- sie wirken als Ausloeser der
// Live-Vorschau nur zusammen mit dem array-tauglichen buildCalcKey (useReportCalc.ts, Plan 04
// Task 3): ohne den Fix liefert `.join('|')` fuer ein Array von Objekten immer denselben String
// ("[object Object]"), der Schluessel aendert sich beim Tippen in der Brennstoffmatrix nie.
// Phase 34: `alternativeFuels` ist jetzt umso wichtiger -- an ihm haengt, dass die Live-Vorschau
// nach einem Kategoriewechsel oder einer neuen Zeile ueberhaupt feuert (der Sonstige-Block ist
// entfallen, es gibt keinen zweiten Eintrag mehr dafuer).
const ENVIRONMENTAL_CALC_INPUT_KEYS = [
  'waterWithdrawal',
  'waterDischarge',
  'primaryFuels',
  'alternativeFuels',
  // Phase 31: ohne diese beiden Eintraege aendert sich der Debounce-Schluessel beim Tippen in
  // einer Einsatzstoffzelle nie und die Live-Vorschau der vier Rohstoffsummen feuert nicht --
  // dasselbe Symptom, das Phase 30 fuer die drei Brennstoff-Arrays oben geloest hat.
  'inputMaterials',
  'otherInputMaterials',
  // Phase 32 (P-02/SB-02): ohne diese vier Eintraege aendert sich der Debounce-Schluessel beim
  // Tippen im Klinkerlagerbestands- oder Rekultivierungsfeld nie, und die Live-Vorschau der
  // beiden neuen berechneten Werte feuert nicht -- dasselbe Symptom, das Phase 30 fuer die
  // Brennstoff-Arrays und Phase 31 fuer die Einsatzstoffe geloest haben.
  // Die beiden Klinkerlagerbestandsfelder existierten schon vor dieser Phase und fehlten
  // trotzdem hier.
  'clinkerStockJan01',
  'clinkerStockDec31',
  'rehabilitatedAreaPriorYears',
  'rehabilitatedAreaCurrentYear',
  // Phase 38 (D-19): ohne diesen Eintrag aendert sich der Debounce-Schluessel beim Tippen in
  // einem Frachten-Feld nie und die Live-Vorschau von Feld 4 und den 33 Frachten feuert nicht --
  // dasselbe Symptom, das Phase 30 fuer die Brennstoff-Arrays und Phase 31/32 fuer die
  // Einsatzstoffe/Klinkerlagerbestandsfelder geloest haben. `buildCalcKey` traegt Objekte
  // bereits ueber `JSON.stringify` (useReportCalc.ts), es braucht keine weitere Aenderung.
  // Erwartungsgrenze: der Payload traegt damit ALLE Oefen der Meldung samt Messungen, auch eine
  // Aenderung an einem Ofenfeld ausserhalb des Rechenblatts loest eine Vorschau-Anfrage aus --
  // Ueber-, nie Untererkennung, wie im Backend bei extractInputs().
  'rotaryKilns',
] as const;

// Alle 16 Bundeslandschluessel, identisch zu Object.keys(FederalStates).
const FORMULAR_AB_CALC_INPUT_KEYS = Object.keys(FederalStates);

// Die vier isChild===false-Kategorien aus ProductCategoryConfig, identisch zu
// FormularCCalculator::PARENT_KEYS (Backend).
const FORMULAR_C_CALC_INPUT_KEYS = [
  'readyMixConcrete', 'concreteComponents', 'otherSiloCement', 'baggedCement',
] as const;

/**
 * REPORT_TYPES: ein Deskriptor je Report-Typ (D-02). Ersetzt die sechs verstreuten
 * switch(reportType)-Bloecke in ReportEditPage.tsx (fetchReport, Werksliste, handleSave,
 * handleSubmit, handleApprove, renderForm).
 *
 * Ein `validate`-Feld traegt der Deskriptor seit 20-04 (K11/D-08) nicht mehr: die
 * Vollstaendigkeitspruefung beim Einreichen liegt vollstaendig im Backend.
 *
 * Das Feld heisst identisch zu ReportPayloadValidator::TYPE_MAP und den neuen Backend-
 * supportsKind-Werten — NICHT identisch zum Record-Key (ReportTypeIdentifier, z.B.
 * 'formular-ab' mit Bindestrich statt Unterstrich).
 *
 * Mit 18-08 (D-11-Schritt 5, FormularD) laeuft jedes der fuenf Formulare ueber genau eine
 * Prop-Grenze (`form`) -- der Per-Tastendruck-Lift und das ihn zuvor markierende Flag am
 * Deskriptor existieren nirgends mehr (Pitfall 3 strukturell aufgeloest).
 *
 * Bewusst NICHT vereinheitlicht (18-CONTEXT.md "Integration Points"): plant +
 * initialCalculations (nur Unfall) bleiben Sonderfaelle am Form-Aufruf in der Page, nicht Teil
 * dieses Deskriptors. Der Vergleichswert Inlandsversand (Versand nach Produktkategorien,
 * Zementartenstatistik) kommt aus der Rechenantwort (reportId an /api/reports/calculate).
 */
export const REPORT_TYPES = {
  [ReportTypeIdentifier.ACCIDENT]: {
    kind: 'accident',
    api: accidentDataApi,
    hydrate: hydrateAccidentData,
    Form: AccidentDataForm,
    calcInputKeys: ACCIDENT_CALC_INPUT_KEYS,
  },
  [ReportTypeIdentifier.ENVIRONMENTAL]: {
    kind: 'environmental',
    api: environmentalDataApi,
    hydrate: hydrateEnvironmentalData,
    Form: EnvironmentalDataForm,
    calcInputKeys: ENVIRONMENTAL_CALC_INPUT_KEYS,
  },
  [ReportTypeIdentifier.FORMULAR_AB]: {
    kind: 'formular_ab',
    api: formularABApi,
    hydrate: hydrateFormularAB,
    Form: FormularABForm,
    calcInputKeys: FORMULAR_AB_CALC_INPUT_KEYS,
  },
  [ReportTypeIdentifier.FORMULAR_C]: {
    kind: 'formular_c',
    api: formularCApi,
    hydrate: hydrateFormularC,
    Form: FormularCForm,
    calcInputKeys: FORMULAR_C_CALC_INPUT_KEYS,
  },
  [ReportTypeIdentifier.FORMULAR_D]: {
    // Seit Plan 23-01 gibt es FormularDCalculator (D-17); die 277 Schluessel unten sind alle
    // schreibbaren Felder ausser `beitragspflichtig` (Calculator-AUSGABE, seit 23-05
    // report:read). Abgeleitet in formularD/catalog.ts, nicht hier erneut aufgezaehlt (D-26).
    kind: 'formular_d',
    api: formularDApi,
    hydrate: hydrateFormularD,
    Form: FormularDForm,
    calcInputKeys: FORMULAR_D_CALC_INPUT_KEYS,
  },
} as const;

/** Deskriptor-Typ, aus REPORT_TYPES abgeleitet -- kein separates, redundant getipptes Interface. */
export type ReportTypeDescriptor = (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];
